/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSignals} from '@preact/signals-react/runtime';
import isEqual from 'lodash.isequal';
import {
  globalChooserSignal,
  modbusFormSignal,
  openGlobalChooserSignal,
  shouldValidateAccountOpen
} from '@ion/applications/src/components/modbus/store';
import DashboardFrameManager from '@ion/charting/src/bis/dashboard/DashboardFrameManager';
import usePersistentIFrame from '@ion/dls/src/client/usePersistentIFrame';
import usePersistentSDKIFrame from '@ion/dls/src/client/usePersistentSDKIFrame';
import {GlobalGroupChooser} from '@ion/dls/src/components/groupchooser/GlobalGroupChooser';
import {groupChooserSelector} from '@/store/selectors/global-group';
import {setGlobalGroup, showChangeGroup} from '../store/actions/global-group-chooser';

/**
 * @GlobalAccountChooserDrawer
 * It will display GlobalAccountChooserDrawer
 * ```typescript
 *   import GlobalAccountChooserDrawer from '@ion/packages/ion-ctc-admin/src/header/GlobalAccountChooserDrawer';
 *
 *   <GlobalAccountChooserDrawer />;
 *
 * ```
 */
export const GlobalAccountChooserDrawer = () => {
  const dispatch = useDispatch();

  const {
    accountHierarchy,
    globalGroup,
    groupChooser : {
      isViewing: openChooser
    }
  } = useSelector(groupChooserSelector);

  const {removePersistentIFrames}    = usePersistentIFrame({});
  const {removePersistentSDKIFrames} = usePersistentSDKIFrame({});
  useSignals();

  useEffect(() => {
    if (shouldValidateAccountOpen?.value) {
      dispatch(showChangeGroup(!openChooser));

      openGlobalChooserSignal.value = {
        shouldOpen : false
      };
    }
  }, [shouldValidateAccountOpen?.value, openChooser]);

  const onClick = useCallback(function() {
    const currentPath = window?.location?.pathname;
    const hasModbus   = isEqual(currentPath, '/toolbox/modbus');
    if (modbusFormSignal?.value?.shouldValidate && hasModbus) {
      globalChooserSignal.value = {
        shouldConfirm : true
      };
    } else {
      dispatch(showChangeGroup(!openChooser));
    }
  }, [openChooser]);

  const onCloseDrawer = useCallback(function() {
    dispatch(showChangeGroup(false));
  }, []);

  const selectGroup = useCallback(function(group) {
    DashboardFrameManager.clearAllFrameInstances();
    removePersistentIFrames();
    removePersistentSDKIFrames();
    dispatch(setGlobalGroup(group) as any);
  }, []);

  return (
    <GlobalGroupChooser allCrumbsClickable
      accountHierarchy={accountHierarchy}
      selectedGroup={globalGroup}
      drawerOpen={openChooser}
      onDrawerClick={onClick}
      onCloseDrawer={onCloseDrawer}
      onSelectGroup={selectGroup}
      hideParentAccount
      includeSelectedAccountId
      includeBreadcrumbs
      disableAutoSelect={false} />
  );
};

export default React.memo(GlobalAccountChooserDrawer);
